import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ConfirmModal } from "../components/organisms/ConfirmModal";
import { createCommandRegistryStore } from "../services/commands/commandRegistryStore";
import type { ConfirmCopy } from "../services/commands/commandRegistryStore";
import type { CommandAction, CommandRegistryApi, KeyChord } from "../services/commands/commands.types";
import { isMacPlatform } from "../services/commands/commands.types";
import { goToShortcut, useAppCommands } from "../hooks/useAppCommands";
import { getCharacterKeysEnabled } from "../services/commands/characterKeys";
import { countOpenModals, findSequenceAction, findShortcutAction, isFieldTarget, startsSequence } from "../services/commands/keyboard";
import { CommandRegistryContext, useCommandRegistry, useRegisterCommands } from "./CommandRegistryContext";
import { CommandFreeTextContext } from "./CommandFreeTextContext";
import type { CommandFreeTextApi, FreeTextHandler } from "./CommandFreeTextContext";

interface PendingConfirm extends ConfirmCopy { settle: (accepted: boolean) => void }

/** How long after the leader key ("G") the second key still counts. */
const SEQUENCE_WINDOW_MS = 1500;
const STORIES_PATH = /^\/stories(\/|$)/;

/** The app-wide actions: open the palette, show shortcuts, go to stories. */
const AppCommands: React.FC = () => {
  const { ui } = useCommandRegistry();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const actions: CommandAction[] = [
    {
      id: "palette.open", label: "Open actions", group: "navigation", keywords: ["palette", "search", "find"],
      patterns: ["open actions", "open commands"],
      shortcut: { chords: [{ key: "k", mod: true }], allowInFields: true },
      isAvailable: () => ({ status: "ready" }),
      // The key toggles; typed or palette runs only open.
      run: (_args, ctx) => {
        const close = ctx.source === "keyboard" && ui.paletteOpen;
        ui.setShortcutsOpen(false);
        ui.setPaletteOpen(!close);
        return { status: "done", message: close ? "Closed commands." : "Opened commands." };
      },
    },
    {
      // Same entry as the help menu's "Voice commands": the command list carries the mic.
      id: "help.voice", label: "Voice commands", group: "help", keywords: ["voice", "speak", "microphone"],
      patterns: ["voice commands"],
      isAvailable: () => ({ status: "ready" }),
      run: () => {
        ui.setShortcutsOpen(false);
        ui.setPaletteOpen(true);
        return { status: "done", message: "Opened commands." };
      },
    },
    {
      id: "help.shortcuts", label: "Keyboard shortcuts", group: "help", keywords: ["keys", "hotkeys", "help"],
      patterns: ["show shortcuts", "keyboard shortcuts"],
      shortcut: { chords: [{ key: "?" }] },
      isAvailable: () => ({ status: "ready" }),
      run: () => {
        ui.setPaletteOpen(false);
        ui.setShortcutsOpen(true);
        return { status: "done", message: "Showing keyboard shortcuts." };
      },
    },
    {
      id: "nav.stories", label: "Go to stories", group: "navigation", keywords: ["home", "list"],
      patterns: ["go to stories"],
      shortcut: goToShortcut("s"),
      isAvailable: () => (STORIES_PATH.test(pathname) ? { status: "hidden" } : { status: "ready" }),
      run: () => {
        navigate("/stories");
        return { status: "done", message: "Went to stories." };
      },
    },
  ];
  useRegisterCommands(actions);
  useAppCommands();
  return null;
};

/** Owns the command store, the palette and shortcuts-dialog open state, and the one window keydown listener. */
export const CommandRegistryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [freeText, setFreeText] = useState<FreeTextHandler | null>(null);
  const [insetLeft, setInsetLeft] = useState<string | undefined>(undefined);
  const pendingRef = useRef<PendingConfirm | null>(null);
  pendingRef.current = pending;

  const confirm = useCallback((copy: ConfirmCopy) => new Promise<boolean>((resolve) => {
    pendingRef.current?.settle(false);
    setPending({ ...copy, settle: resolve });
  }), []);
  const store = useMemo(() => createCommandRegistryStore({ confirm }), [confirm]);

  const uiRef = useRef({ paletteOpen, shortcutsOpen });
  uiRef.current = { paletteOpen, shortcutsOpen };
  useEffect(() => {
    const mac = isMacPlatform();
    // "G" then a letter: the first key arms the leader for a moment; the next key runs the sequence it completes.
    let leader: { chord: KeyChord; timer: number } | null = null;
    const clearLeader = (): void => {
      if (leader) window.clearTimeout(leader.timer);
      leader = null;
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      const modals = countOpenModals();
      if (!isFieldTarget(event.target) && modals === 0 && getCharacterKeysEnabled()) {
        if (leader) {
          const sequence = findSequenceAction(leader.chord, event, store.list(), mac);
          clearLeader();
          if (sequence) {
            event.preventDefault();
            if (sequence.isAvailable({ source: "keyboard" }).status === "ready") void store.run(sequence.id, {}, { source: "keyboard" });
            return;
          }
        }
        if (startsSequence(event, store.list(), mac)) {
          clearLeader();
          leader = { chord: { key: event.key.toLowerCase() }, timer: window.setTimeout(clearLeader, SEQUENCE_WINDOW_MS) };
          return;
        }
      }
      const own = (uiRef.current.paletteOpen ? 1 : 0) + (uiRef.current.shortcutsOpen ? 1 : 0);
      const action = findShortcutAction(event, store.list(), {
        mac, characterKeys: getCharacterKeysEnabled(), inField: isFieldTarget(event.target), modalOpen: modals > 0, foreignModalOpen: modals > own,
      });
      if (!action || action.isAvailable({ source: "keyboard" }).status !== "ready") return;
      event.preventDefault();
      void store.run(action.id, {}, { source: "keyboard" });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      clearLeader();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [store]);

  const value = useMemo<CommandRegistryApi>(() => ({
    ...store, ui: { paletteOpen, shortcutsOpen, setPaletteOpen, setShortcutsOpen },
  }), [store, paletteOpen, shortcutsOpen]);

  // Stable, so a registering effect does not re-run (and re-register) each time the handler changes.
  const registerFreeText = useCallback((handler: FreeTextHandler) => {
    setFreeText(() => handler);
    return () => setFreeText((current) => (current === handler ? null : current));
  }, []);
  const registerInset = useCallback((value: string) => {
    setInsetLeft(value);
    return () => setInsetLeft((current) => (current === value ? undefined : current));
  }, []);
  const freeTextApi = useMemo<CommandFreeTextApi>(
    () => ({ handler: freeText, register: registerFreeText, insetLeft, registerInset }),
    [freeText, registerFreeText, insetLeft, registerInset],
  );

  const answer = (accepted: boolean): void => {
    pending?.settle(accepted);
    setPending(null);
  };
  return (
    <CommandRegistryContext.Provider value={value}>
      <CommandFreeTextContext.Provider value={freeTextApi}>
        <AppCommands />
        {children}
        <ConfirmModal
          open={pending !== null}
          title={pending?.title ?? ""}
          description={pending?.body ?? ""}
          confirmLabel="Continue"
          cancelLabel="Cancel"
          onConfirm={() => answer(true)}
          onCancel={() => answer(false)}
        />
      </CommandFreeTextContext.Provider>
    </CommandRegistryContext.Provider>
  );
};
